import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const MaintenanceCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date(2025, 7, 16)); // August 16, 2025
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month');

  const maintenanceEvents = [
    {
      id: 1,
      vehicleId: 'FL-001',
      type: 'Oil Change',
      date: new Date(2025, 7, 18),
      priority: 'medium',
      technician: 'Mike Johnson',
      estimatedTime: '2 hours'
    },
    {
      id: 2,
      vehicleId: 'FL-003',
      type: 'Brake Inspection',
      date: new Date(2025, 7, 20),
      priority: 'high',
      technician: 'Sarah Wilson',
      estimatedTime: '3 hours'
    },
    {
      id: 3,
      vehicleId: 'FL-002',
      type: 'Tire Rotation',
      date: new Date(2025, 7, 22),
      priority: 'low',
      technician: 'David Chen',
      estimatedTime: '1 hour'
    },
    {
      id: 4,
      vehicleId: 'FL-004',
      type: 'Engine Diagnostic',
      date: new Date(2025, 7, 25),
      priority: 'high',
      technician: 'Lisa Rodriguez',
      estimatedTime: '4 hours'
    },
    {
      id: 5,
      vehicleId: 'FL-001',
      type: 'Annual Inspection',
      date: new Date(2025, 7, 28),
      priority: 'medium',
      technician: 'Mike Johnson',
      estimatedTime: '6 hours'
    }
  ];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)?.getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1)?.getDay();
  };

  const getEventsForDate = (date) => {
    return maintenanceEvents?.filter(event => 
      event?.date?.toDateString() === date?.toDateString()
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-error';
      case 'medium': return 'bg-warning';
      case 'low': return 'bg-success';
      default: return 'bg-muted-foreground';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return 'AlertTriangle';
      case 'medium': return 'Clock';
      case 'low': return 'CheckCircle';
      default: return 'Circle';
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentDate?.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days?.push(<div key={`empty-${i}`} className="h-20 border border-border"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const events = getEventsForDate(date);
      const isToday = date?.toDateString() === new Date()?.toDateString();
      const isSelected = selectedDate && date?.toDateString() === selectedDate?.toDateString();

      days?.push(
        <div
          key={day}
          className={`h-20 border border-border p-1 cursor-pointer hover:bg-muted nav-transition ${
            isToday ? 'bg-accent/10 border-accent' : ''
          } ${isSelected ? 'bg-primary/10 border-primary' : ''}`}
          onClick={() => setSelectedDate(date)}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-accent' : 'text-foreground'}`}>
            {day}
          </div>
          <div className="space-y-1">
            {events?.slice(0, 2)?.map((event, index) => (
              <div
                key={event?.id}
                className={`text-xs px-1 py-0.5 rounded text-white truncate ${getPriorityColor(event?.priority)}`}
                title={`${event?.vehicleId} - ${event?.type}`}
              >
                {event?.vehicleId}
              </div>
            ))}
            {events?.length > 2 && (
              <div className="text-xs text-muted-foreground">+{events?.length - 2} more</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const upcomingEvents = maintenanceEvents?.filter(event => event?.date >= new Date())?.sort((a, b) => a?.date - b?.date)?.slice(0, 5);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Maintenance Schedule</h3>
        
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                viewMode === 'month' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-xs font-medium rounded-md nav-transition ${
                viewMode === 'list' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              List
            </button>
          </div>
        </div>
      </div>
      {viewMode === 'month' ? (
        <>
          {/* Calendar Navigation */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <button
              onClick={() => navigateMonth(-1)}
              className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg nav-transition"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            
            <h4 className="text-lg font-semibold text-foreground">
              {currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h4>
            
            <button
              onClick={() => navigateMonth(1)}
              className="flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg nav-transition"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="p-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-0 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
                <div key={day} className="h-8 flex items-center justify-center text-sm font-medium text-muted-foreground">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-0 border border-border rounded-lg overflow-hidden">
              {renderCalendarDays()}
            </div>
          </div>
        </>
      ) : (
        /* List View */
        (<div className="max-h-96 overflow-y-auto">
          {upcomingEvents?.map((event) => (
            <div key={event?.id} className="flex items-center justify-between p-4 border-b border-border last:border-b-0 hover:bg-muted nav-transition">
              <div className="flex items-center space-x-3">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${getPriorityColor(event?.priority)}`}>
                  <Icon name={getPriorityIcon(event?.priority)} size={16} color="white" />
                </div>
                
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-foreground">{event?.vehicleId}</span>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm text-foreground">{event?.type}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <Icon name="User" size={12} />
                    <span>{event?.technician}</span>
                    <span>•</span>
                    <Icon name="Clock" size={12} />
                    <span>{event?.estimatedTime}</span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">
                  {event?.date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div className="text-xs text-muted-foreground">
                  {event?.date?.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
              </div>
            </div>
          ))}
        </div>)
      )}
      {/* Footer */}
      <div className="p-4 bg-muted border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-error rounded-full"></div>
              <span className="text-xs text-muted-foreground">High Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <span className="text-xs text-muted-foreground">Medium Priority</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span className="text-xs text-muted-foreground">Low Priority</span>
            </div>
          </div>
          
          <div className="text-xs text-muted-foreground">
            {maintenanceEvents?.length} scheduled events
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceCalendar;